import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, Minus, Shield, Wifi, DollarSign, Home, Heart, Globe, ArrowRight, BarChart3, HelpCircle, Laptop, Users, ChevronDown, ChevronUp, MapPin } from 'lucide-react';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { SEOHead } from '../components/common/SEOHead';
import { ComparisonInternalLinks } from '../components/common/InternalLinkingSections';
import { formatCurrency } from '../lib/utils';
import { safeFetch } from '../config/api';

// Helper function to generate canonical comparison slug
const getCanonicalComparisonSlug = (slugA, slugB) => {
  const slugs = [slugA, slugB].sort();
  return `${slugs[0]}-vs-${slugs[1]}`;
};

export default function CityComparisonPage() {
  const { comparison } = useParams();
  const navigate = useNavigate();
  const [cityA, setCityA] = useState(null);
  const [cityB, setCityB] = useState(null);
  const [allCities, setAllCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [relatedComparisons, setRelatedComparisons] = useState([]);

  // Parse the comparison slug (e.g., "lisbon-vs-barcelona")
  const parseComparison = (slug) => {
    if (!slug) return { cityASlug: null, cityBSlug: null };
    const parts = slug.split('-vs-');
    if (parts.length !== 2) return { cityASlug: null, cityBSlug: null };
    return { cityASlug: parts[0], cityBSlug: parts[1] };
  };

  const { cityASlug, cityBSlug } = parseComparison(comparison);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First check canonical URL and redirect if needed
        if (cityASlug && cityBSlug) {
          const canonicalSlug = getCanonicalComparisonSlug(cityASlug, cityBSlug);
          if (comparison !== canonicalSlug) {
            // Redirect to canonical URL
            navigate(`/compare/${canonicalSlug}`, { replace: true });
            return;
          }
        }

        // Fetch comparison data from backend (this also registers for sitemap)
        const comparisonResponse = await safeFetch(`/api/compare/${comparison}`);
        
        if (comparisonResponse.status === 404) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        
        if (!comparisonResponse.ok) {
          throw new Error('Failed to fetch comparison data');
        }
        
        const comparisonData = await comparisonResponse.json();
        
        // Handle redirect response from backend
        if (comparisonData.redirect) {
          navigate(comparisonData.canonical_url, { replace: true });
          return;
        }
        
        setCityA(comparisonData.city_a);
        setCityB(comparisonData.city_b);

        // Fetch related comparisons (15 links)
        try {
          const relatedResponse = await safeFetch(`/api/compare/${comparison}/related?limit=15`);
          if (relatedResponse.ok) {
            const relatedData = await relatedResponse.json();
            setRelatedComparisons(relatedData.related_comparisons || []);
          }
        } catch (err) {
          console.error('Failed to fetch related comparisons:', err);
        }

        // Fetch AI analysis (in background)
        setAnalysisLoading(true);
        try {
          const analysisResponse = await safeFetch(`/api/compare/${comparison}/analysis`);
          if (analysisResponse.ok) {
            const analysisData = await analysisResponse.json();
            if (analysisData.analysis) {
              setAiAnalysis(analysisData.analysis);
            }
          }
        } catch (err) {
          console.error('Failed to fetch AI analysis:', err);
        } finally {
          setAnalysisLoading(false);
        }

        // Fetch all cities for related comparisons fallback. Lite shape
        // is sufficient — the related-comparisons sidebar only needs slug,
        // name, country_name, nomad_score + basic cost fields, all of
        // which live in the lite payload (~110 KB saved vs the full list).
        const citiesResponse = await safeFetch(`/api/cities?lite=true`);
        if (citiesResponse.ok) {
          const cities = await citiesResponse.json();
          setAllCities(cities);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (cityASlug && cityBSlug) {
      fetchData();
    } else {
      setError('Invalid comparison URL');
      setLoading(false);
    }
  }, [comparison, cityASlug, cityBSlug, navigate]);

  const currentYear = new Date().getFullYear();

  // Calculate total costs
  const getTotalCost = (city) => {
    if (!city) return 0;
    return city.rent_1br + city.groceries_cost + city.transport_cost + city.healthcare_cost + city.internet_cost;
  };

  // Compare values and return indicator
  const compareValues = (valueA, valueB, lowerIsBetter = false) => {
    if (valueA === valueB) return { winner: 'tie', icon: Minus, color: 'text-slate-400' };
    const aWins = lowerIsBetter ? valueA < valueB : valueA > valueB;
    return {
      winner: aWins ? 'A' : 'B',
      icon: aWins ? TrendingUp : TrendingDown,
      color: aWins ? 'text-emerald-500' : 'text-rose-500'
    };
  };

  // Generate FAQs for the comparison
  const generateFAQs = () => {
    if (!cityA || !cityB) return [];
    const totalA = getTotalCost(cityA);
    const totalB = getTotalCost(cityB);
    const cheaperCity = totalA < totalB ? cityA : cityB;
    const expensiveCity = totalA < totalB ? cityB : cityA;
    const costDiff = Math.abs(totalA - totalB);
    const saferCity = cityA.safety_score > cityB.safety_score ? cityA : cityB;
    const fasterInternetCity = cityA.internet_speed > cityB.internet_speed ? cityA : cityB;
    
    return [
      {
        question: `Is ${cityA.name} or ${cityB.name} cheaper to live in?`,
        answer: `${cheaperCity.name} is ${Math.round((costDiff / Math.max(totalA, totalB)) * 100)}% cheaper than ${expensiveCity.name}. The average monthly cost in ${cheaperCity.name} is ${formatCurrency(getTotalCost(cheaperCity))}, while ${expensiveCity.name} costs ${formatCurrency(getTotalCost(expensiveCity))} per month.`
      },
      {
        question: `Which city is better for digital nomads: ${cityA.name} or ${cityB.name}?`,
        answer: `For digital nomads, ${fasterInternetCity.name} offers faster internet at ${fasterInternetCity.internet_speed} Mbps. ${cheaperCity.name} is more budget-friendly with lower living costs. Both cities have their strengths - ${cityA.name} has a NomadIQ score of ${cityA.nomad_score}/10 while ${cityB.name} scores ${cityB.nomad_score}/10.`
      },
      {
        question: `What is the rent difference between ${cityA.name} and ${cityB.name}?`,
        answer: `A 1-bedroom apartment costs ${formatCurrency(cityA.rent_1br)}/month in ${cityA.name} and ${formatCurrency(cityB.rent_1br)}/month in ${cityB.name}. ${cityA.rent_1br < cityB.rent_1br ? cityA.name : cityB.name} has ${Math.round(Math.abs(cityA.rent_1br - cityB.rent_1br) / Math.max(cityA.rent_1br, cityB.rent_1br) * 100)}% lower rent.`
      },
      {
        question: `Is ${cityA.name} or ${cityB.name} safer?`,
        answer: `${saferCity.name} has a higher safety score of ${saferCity.safety_score}/10. ${cityA.name} scores ${cityA.safety_score}/10 for safety while ${cityB.name} scores ${cityB.safety_score}/10.`
      },
      {
        question: `Should I move to ${cityA.name} or ${cityB.name} as an expat?`,
        answer: `The choice depends on your priorities. Choose ${cheaperCity.name} if budget is important (${formatCurrency(getTotalCost(cheaperCity))}/month). Choose ${saferCity.name} for better safety. ${fasterInternetCity.name} is better for remote work with ${fasterInternetCity.internet_speed} Mbps internet.`
      }
    ];
  };

  // Generate schema with FAQ
  const generateSchema = () => {
    if (!cityA || !cityB) return null;
    const faqs = generateFAQs();
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": `${cityA.name} vs ${cityB.name}: Cost of Living & Expat Comparison`,
        "author": { "@type": "Organization", "name": "MoveAbroadIQ" },
        "publisher": { "@type": "Organization", "name": "MoveAbroadIQ" },
        "datePublished": new Date().toISOString().split('T')[0],
        "description": `Compare living in ${cityA.name} vs ${cityB.name}. Discover cost of living, rent, safety, internet speed and digital nomad lifestyle.`
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ];
  };

  // Get other comparison suggestions (with canonical URLs)
  const getRelatedComparisons = () => {
    if (!cityA || !cityB || allCities.length === 0) return [];
    const otherCities = allCities.filter(c => c.slug !== cityA.slug && c.slug !== cityB.slug);
    const comparisons = [];
    
    // Add comparisons with cityA
    otherCities.slice(0, 2).forEach(city => {
      const canonicalSlug = getCanonicalComparisonSlug(cityA.slug, city.slug);
      comparisons.push({
        url: `/compare/${canonicalSlug}`,
        label: `${cityA.name} vs ${city.name}`
      });
    });
    
    // Add comparisons with cityB
    otherCities.slice(0, 2).forEach(city => {
      const canonicalSlug = getCanonicalComparisonSlug(cityB.slug, city.slug);
      if (!comparisons.find(c => c.url.includes(canonicalSlug))) {
        comparisons.push({
          url: `/compare/${canonicalSlug}`,
          label: `${cityB.name} vs ${city.name}`
        });
      }
    });
    
    return comparisons.slice(0, 4);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <SEOHead 
          title="City Not Found | MoveAbroadIQ"
          description="The requested city comparison could not be found."
          canonicalPath="/tools/compare"
          noindex={true}
        />
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">City Not Found</h1>
          <p className="text-slate-600 mb-8">
            One or both of the cities in this comparison don't exist in our database.
          </p>
          <Link 
            to="/tools/compare" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Browse Available Comparisons
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !cityA || !cityB) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Comparison Not Found</h1>
          <p className="text-slate-600 mb-8">{error || 'Unable to load city comparison'}</p>
          <Link to="/tools/compare" className="text-blue-600 hover:text-blue-800">
            ← Go to Country Comparison Tool
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const totalCostA = getTotalCost(cityA);
  const totalCostB = getTotalCost(cityB);
  const costComparison = compareValues(totalCostA, totalCostB, true);
  const safetyComparison = compareValues(cityA.safety_score, cityB.safety_score);
  const internetComparison = compareValues(cityA.internet_speed, cityB.internet_speed);
  const nomadScoreComparison = compareValues(cityA.nomad_score, cityB.nomad_score);

  // Calculate canonical slug for SEO
  const canonicalSlug = getCanonicalComparisonSlug(cityA.slug, cityB.slug);

  const comparisonRows = [
    { label: 'Total Monthly Cost', valueA: formatCurrency(totalCostA), valueB: formatCurrency(totalCostB), comparison: costComparison, icon: DollarSign },
    { label: 'Rent (1BR)', valueA: formatCurrency(cityA.rent_1br), valueB: formatCurrency(cityB.rent_1br), comparison: compareValues(cityA.rent_1br, cityB.rent_1br, true), icon: Home },
    { label: 'Groceries', valueA: formatCurrency(cityA.groceries_cost), valueB: formatCurrency(cityB.groceries_cost), comparison: compareValues(cityA.groceries_cost, cityB.groceries_cost, true), icon: null },
    { label: 'Transport', valueA: formatCurrency(cityA.transport_cost), valueB: formatCurrency(cityB.transport_cost), comparison: compareValues(cityA.transport_cost, cityB.transport_cost, true), icon: null },
    { label: 'Healthcare', valueA: formatCurrency(cityA.healthcare_cost), valueB: formatCurrency(cityB.healthcare_cost), comparison: compareValues(cityA.healthcare_cost, cityB.healthcare_cost, true), icon: Heart },
    { label: 'Safety Score', valueA: `${cityA.safety_score}/10`, valueB: `${cityB.safety_score}/10`, comparison: safetyComparison, icon: Shield },
    { label: 'Internet Speed', valueA: `${cityA.internet_speed} Mbps`, valueB: `${cityB.internet_speed} Mbps`, comparison: internetComparison, icon: Wifi },
    { label: 'NomadIQ Score', valueA: `${cityA.nomad_score}/10`, valueB: `${cityB.nomad_score}/10`, comparison: nomadScoreComparison, icon: Globe },
  ];

  // Determine overall winner
  let cityAWins = 0;
  let cityBWins = 0;
  comparisonRows.forEach(row => {
    if (row.comparison.winner === 'A') cityAWins++;
    else if (row.comparison.winner === 'B') cityBWins++;
  });

  const overallWinner = cityAWins > cityBWins ? cityA : cityBWins > cityAWins ? cityB : null;

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title={`${cityA.name} vs ${cityB.name} – Cost of Living, Safety & Digital Nomad Comparison (${currentYear})`}
        description={`Compare living in ${cityA.name} vs ${cityB.name}. Discover cost of living, rent, safety, internet speed and digital nomad lifestyle. Make informed relocation decisions.`}
        canonicalPath={`/compare/${canonicalSlug}`}
        schema={generateSchema()}
      />
      <Navbar />

      {/* Header */}
      <section className="bg-gradient-to-br from-indigo-50 to-slate-50 py-12 md:py-16 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/tools/compare"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Comparison Tool
          </Link>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-indigo-100 flex items-center justify-center">
              <BarChart3 className="w-7 h-7 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight" data-testid="comparison-title">
                {cityA.name} vs {cityB.name}
              </h1>
              <p className="text-lg text-slate-600">
                Cost of Living, Safety & Expat Guide ({currentYear})
              </p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-4 mt-8">
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center gap-3 mb-3">
                <img 
                  src={cityA.image_url || '/placeholder-city.svg'} 
                  alt={cityA.name}
                  className="w-12 h-12 rounded-lg object-cover"
                />
                <div>
                  <h2 className="font-bold text-slate-900">{cityA.name}</h2>
                  <p className="text-sm text-slate-500">{cityA.country_name}</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-slate-900">{formatCurrency(totalCostA)}/mo</span>
                <span className="text-sm bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
                  {cityA.nomad_score}/10
                </span>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center gap-3 mb-3">
                <img 
                  src={cityB.image_url || '/placeholder-city.svg'} 
                  alt={cityB.name}
                  className="w-12 h-12 rounded-lg object-cover"
                />
                <div>
                  <h2 className="font-bold text-slate-900">{cityB.name}</h2>
                  <p className="text-sm text-slate-500">{cityB.country_name}</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-slate-900">{formatCurrency(totalCostB)}/mo</span>
                <span className="text-sm bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
                  {cityB.nomad_score}/10
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* City Images Section */}
      <section className="py-8 md:py-12 bg-white" data-testid="city-images-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            {/* City A Image Card */}
            <Link 
              to={`/cities/${cityA.slug}`}
              className="group relative overflow-hidden rounded-2xl aspect-[16/10] shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <img 
                src={cityA.image_url || '/placeholder-city.svg'}
                alt={`${cityA.name} skyline`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              {/* City Info Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-1 drop-shadow-lg">
                  {cityA.name}
                </h3>
                <p className="text-white/90 text-lg drop-shadow-md flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {cityA.country_name}
                </p>
                <div className="flex items-center gap-4 mt-3">
                  <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium">
                    {formatCurrency(totalCostA)}/mo
                  </span>
                  <span className="bg-indigo-500/80 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium">
                    NomadIQ {cityA.nomad_score}/10
                  </span>
                </div>
              </div>
              {/* Hover indicator */}
              <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <ArrowRight className="w-5 h-5 text-white" />
              </div>
            </Link>

            {/* City B Image Card */}
            <Link 
              to={`/cities/${cityB.slug}`}
              className="group relative overflow-hidden rounded-2xl aspect-[16/10] shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <img 
                src={cityB.image_url || '/placeholder-city.svg'}
                alt={`${cityB.name} skyline`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              {/* City Info Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-1 drop-shadow-lg">
                  {cityB.name}
                </h3>
                <p className="text-white/90 text-lg drop-shadow-md flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {cityB.country_name}
                </p>
                <div className="flex items-center gap-4 mt-3">
                  <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium">
                    {formatCurrency(totalCostB)}/mo
                  </span>
                  <span className="bg-indigo-500/80 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium">
                    NomadIQ {cityB.nomad_score}/10
                  </span>
                </div>
              </div>
              {/* Hover indicator */}
              <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <ArrowRight className="w-5 h-5 text-white" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Detailed Comparison</h2>
          
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="comparison-table">
            {/* Table Header */}
            <div className="grid grid-cols-4 gap-4 p-4 bg-slate-50 border-b border-slate-200">
              <div className="font-semibold text-slate-600">Factor</div>
              <div className="font-semibold text-slate-900 text-center">{cityA.name}</div>
              <div className="font-semibold text-slate-900 text-center">{cityB.name}</div>
              <div className="font-semibold text-slate-600 text-center">Winner</div>
            </div>

            {/* Table Rows */}
            {comparisonRows.map((row, index) => (
              <div 
                key={row.label} 
                className={`grid grid-cols-4 gap-4 p-4 items-center ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
              >
                <div className="flex items-center gap-2">
                  {row.icon && <row.icon className="w-4 h-4 text-slate-400" />}
                  <span className="text-slate-700">{row.label}</span>
                </div>
                <div className={`text-center font-medium ${row.comparison.winner === 'A' ? 'text-emerald-600' : 'text-slate-900'}`}>
                  {row.valueA}
                </div>
                <div className={`text-center font-medium ${row.comparison.winner === 'B' ? 'text-emerald-600' : 'text-slate-900'}`}>
                  {row.valueB}
                </div>
                <div className="flex justify-center">
                  {row.comparison.winner === 'A' && (
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">{cityA.name}</span>
                  )}
                  {row.comparison.winner === 'B' && (
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">{cityB.name}</span>
                  )}
                  {row.comparison.winner === 'tie' && (
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Tie</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Winner Summary */}
          {overallWinner && (
            <div className="mt-8 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="text-xl font-bold text-slate-900 mb-2">Overall Winner: {overallWinner.name}</h3>
              <p className="text-slate-600">
                Based on our comparison, <strong>{overallWinner.name}</strong> wins {overallWinner === cityA ? cityAWins : cityBWins} out of {comparisonRows.length} categories. 
                {overallWinner === cityA ? cityB.name : cityA.name} wins {overallWinner === cityA ? cityBWins : cityAWins} categories.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Which City is Better for Digital Nomads - Hero Summary */}
      <section className="py-12 md:py-16" data-testid="nomad-summary">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-2xl p-8 text-white">
            <div className="flex items-center gap-3 mb-4">
              <Laptop className="w-8 h-8 text-indigo-300" />
              <h2 className="text-2xl font-bold">Which city is better for digital nomads?</h2>
            </div>
            
            {(() => {
              const fasterInternet = cityA.internet_speed > cityB.internet_speed ? cityA : cityB;
              const cheaperCity = totalCostA < totalCostB ? cityA : cityB;
              const higherNomadScore = cityA.nomad_score > cityB.nomad_score ? cityA : cityB;
              
              return (
                <div className="space-y-4">
                  <p className="text-indigo-100 text-lg leading-relaxed">
                    For digital nomads choosing between <strong className="text-white">{cityA.name}</strong> and <strong className="text-white">{cityB.name}</strong>, 
                    the answer depends on your priorities. Here's our analysis:
                  </p>
                  
                  <div className="grid md:grid-cols-3 gap-4 mt-6">
                    <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                      <DollarSign className="w-6 h-6 text-emerald-400 mb-2" />
                      <p className="text-sm text-indigo-200 mb-1">Best for Budget</p>
                      <p className="text-xl font-bold">{cheaperCity.name}</p>
                      <p className="text-sm text-indigo-300">{formatCurrency(getTotalCost(cheaperCity))}/mo</p>
                    </div>
                    
                    <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                      <Wifi className="w-6 h-6 text-cyan-400 mb-2" />
                      <p className="text-sm text-indigo-200 mb-1">Best Internet</p>
                      <p className="text-xl font-bold">{fasterInternet.name}</p>
                      <p className="text-sm text-indigo-300">{fasterInternet.internet_speed} Mbps</p>
                    </div>
                    
                    <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                      <Globe className="w-6 h-6 text-purple-400 mb-2" />
                      <p className="text-sm text-indigo-200 mb-1">Best NomadIQ Score</p>
                      <p className="text-xl font-bold">{higherNomadScore.name}</p>
                      <p className="text-sm text-indigo-300">{higherNomadScore.nomad_score}/10</p>
                    </div>
                  </div>
                  
                  <div className="mt-6 p-4 bg-white/5 rounded-xl border border-white/10">
                    <p className="text-indigo-100">
                      <strong className="text-white">Our Recommendation:</strong> {' '}
                      {higherNomadScore.name === cheaperCity.name 
                        ? `${higherNomadScore.name} is the clear winner for digital nomads, offering both affordability and excellent nomad infrastructure.`
                        : `Choose ${cheaperCity.name} if budget is your top priority, or ${fasterInternet.name} if you need the fastest internet for remote work.`
                      }
                    </p>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      {/* AI-Generated Digital Nomad Analysis */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-blue-50 to-indigo-50" data-testid="ai-analysis-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Laptop className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                Digital Nomad Analysis: {cityA.name} vs {cityB.name}
              </h2>
            </div>
            
            {analysisLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                <span className="ml-3 text-slate-500">Generating expert analysis...</span>
              </div>
            ) : aiAnalysis ? (
              <div className="prose prose-slate max-w-none">
                <p className="text-slate-700 leading-relaxed whitespace-pre-line text-base">
                  {aiAnalysis}
                </p>
              </div>
            ) : (
              <div className="text-slate-600 leading-relaxed">
                <p className="mb-4">
                  When comparing <strong>{cityA.name}</strong> and <strong>{cityB.name}</strong> for digital nomads, several factors come into play.
                </p>
                <p className="mb-4">
                  <strong>Cost of Living:</strong> {totalCostA < totalCostB 
                    ? `${cityA.name} offers a more affordable lifestyle at ${formatCurrency(totalCostA)}/month compared to ${cityB.name}'s ${formatCurrency(totalCostB)}/month.`
                    : totalCostB < totalCostA
                    ? `${cityB.name} is the more budget-friendly option at ${formatCurrency(totalCostB)}/month versus ${cityA.name}'s ${formatCurrency(totalCostA)}/month.`
                    : `Both cities have similar monthly costs around ${formatCurrency(totalCostA)}.`
                  }
                </p>
                <p className="mb-4">
                  <strong>Internet & Remote Work:</strong> {cityA.internet_speed > cityB.internet_speed
                    ? `${cityA.name} leads with ${cityA.internet_speed} Mbps average internet speed, ideal for video calls and large file transfers. ${cityB.name} offers ${cityB.internet_speed} Mbps.`
                    : cityB.internet_speed > cityA.internet_speed
                    ? `${cityB.name} provides faster connectivity at ${cityB.internet_speed} Mbps compared to ${cityA.name}'s ${cityA.internet_speed} Mbps.`
                    : `Both cities offer comparable internet speeds around ${cityA.internet_speed} Mbps.`
                  }
                </p>
                <p className="mb-4">
                  <strong>Safety:</strong> With a safety score of {cityA.safety_score > cityB.safety_score ? `${cityA.safety_score}/10, ${cityA.name} provides a more secure environment than ${cityB.name} (${cityB.safety_score}/10)` : cityB.safety_score > cityA.safety_score ? `${cityB.safety_score}/10, ${cityB.name} offers better security than ${cityA.name} (${cityA.safety_score}/10)` : `${cityA.safety_score}/10, both cities offer similar levels of security`}.
                </p>
                <p>
                  <strong>Overall:</strong> {cityA.nomad_score > cityB.nomad_score 
                    ? `${cityA.name} earns a higher NomadIQ score of ${cityA.nomad_score}/10, making it slightly more suited for the digital nomad lifestyle.`
                    : cityB.nomad_score > cityA.nomad_score
                    ? `${cityB.name}'s NomadIQ score of ${cityB.nomad_score}/10 gives it an edge for remote workers.`
                    : `Both cities score similarly on our NomadIQ scale at ${cityA.nomad_score}/10.`
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12 md:py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Cost of Living Comparison */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-600" />
                Cost of Living Comparison
              </h3>
              <p className="text-slate-600 mb-4">
                {totalCostA < totalCostB 
                  ? `${cityA.name} is ${Math.round((1 - totalCostA/totalCostB) * 100)}% cheaper than ${cityB.name}.`
                  : totalCostB < totalCostA
                  ? `${cityB.name} is ${Math.round((1 - totalCostB/totalCostA) * 100)}% cheaper than ${cityA.name}.`
                  : `Both cities have similar costs of living.`
                }
              </p>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>• {cityA.name} rent: {formatCurrency(cityA.rent_1br)}/mo</li>
                <li>• {cityB.name} rent: {formatCurrency(cityB.rent_1br)}/mo</li>
                <li>• Difference: {formatCurrency(Math.abs(cityA.rent_1br - cityB.rent_1br))}/mo</li>
              </ul>
            </div>

            {/* Safety Comparison */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                Safety Comparison
              </h3>
              <p className="text-slate-600 mb-4">
                {cityA.safety_score > cityB.safety_score
                  ? `${cityA.name} has a higher safety score (${cityA.safety_score}/10) compared to ${cityB.name} (${cityB.safety_score}/10).`
                  : cityB.safety_score > cityA.safety_score
                  ? `${cityB.name} has a higher safety score (${cityB.safety_score}/10) compared to ${cityA.name} (${cityA.safety_score}/10).`
                  : `Both cities have similar safety ratings.`
                }
              </p>
              <div className="flex gap-4">
                <div className="flex-1 text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{cityA.safety_score}/10</p>
                  <p className="text-sm text-slate-500">{cityA.name}</p>
                </div>
                <div className="flex-1 text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{cityB.safety_score}/10</p>
                  <p className="text-sm text-slate-500">{cityB.name}</p>
                </div>
              </div>
            </div>

            {/* Digital Nomad Friendliness */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Wifi className="w-5 h-5 text-purple-600" />
                Digital Nomad Friendliness
              </h3>
              <p className="text-slate-600 mb-4">
                For remote workers, internet speed is crucial. 
                {cityA.internet_speed > cityB.internet_speed
                  ? ` ${cityA.name} offers faster internet at ${cityA.internet_speed} Mbps vs ${cityB.internet_speed} Mbps.`
                  : cityB.internet_speed > cityA.internet_speed
                  ? ` ${cityB.name} offers faster internet at ${cityB.internet_speed} Mbps vs ${cityA.internet_speed} Mbps.`
                  : ` Both cities offer similar internet speeds.`
                }
              </p>
              <div className="flex gap-4">
                <div className="flex-1 text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{cityA.internet_speed} Mbps</p>
                  <p className="text-sm text-slate-500">{cityA.name}</p>
                </div>
                <div className="flex-1 text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{cityB.internet_speed} Mbps</p>
                  <p className="text-sm text-slate-500">{cityB.name}</p>
                </div>
              </div>
            </div>

            {/* Which City Is Better */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-indigo-600" />
                Which City Is Better for Expats?
              </h3>
              <p className="text-slate-600 mb-4">
                {overallWinner 
                  ? `Based on our analysis, ${overallWinner.name} edges out as the better choice for expats, winning ${overallWinner === cityA ? cityAWins : cityBWins} of ${comparisonRows.length} comparison factors.`
                  : `Both cities are excellent choices for expats, with each having unique strengths.`
                }
              </p>
              <div className="space-y-2 text-sm">
                <p className="text-slate-600">
                  <strong>{cityA.name}</strong>: Best for {totalCostA < totalCostB ? 'budget-conscious expats' : 'those prioritizing amenities'}
                </p>
                <p className="text-slate-600">
                  <strong>{cityB.name}</strong>: Best for {totalCostB < totalCostA ? 'budget-conscious expats' : 'those prioritizing amenities'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Comparisons */}
      <section className="py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">More City Comparisons</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {(relatedComparisons.length > 0 ? relatedComparisons : getRelatedComparisons()).slice(0, 15).map((comp) => (
              <Link
                key={comp.url}
                to={comp.url}
                className="p-3 bg-slate-50 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors group"
              >
                <span className="text-slate-700 group-hover:text-indigo-600 font-medium text-sm flex items-center justify-between gap-2">
                  <span className="truncate">{comp.label}</span>
                  <ArrowRight className="w-3 h-3 flex-shrink-0" />
                </span>
              </Link>
            ))}
          </div>

          {/* Links to individual city pages */}
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <Link
              to={`/cities/${cityA.slug}`}
              className="p-4 bg-white rounded-xl border border-slate-200 hover:border-blue-300 transition-colors group flex items-center justify-between"
              data-testid={`link-city-${cityA.slug}`}
            >
              <span className="text-slate-700 group-hover:text-blue-600">
                View full guide for {cityA.name}
              </span>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600" />
            </Link>
            <Link
              to={`/cities/${cityB.slug}`}
              className="p-4 bg-white rounded-xl border border-slate-200 hover:border-blue-300 transition-colors group flex items-center justify-between"
              data-testid={`link-city-${cityB.slug}`}
            >
              <span className="text-slate-700 group-hover:text-blue-600">
                View full guide for {cityB.name}
              </span>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600" />
            </Link>
          </div>

          {/* Additional City Page Links */}
          <div className="mt-6 grid md:grid-cols-4 gap-4">
            <Link
              to={`/cost-of-living/${cityA.slug}`}
              className="p-3 bg-slate-50 rounded-lg border border-slate-100 hover:border-blue-200 transition-colors text-center text-sm text-slate-600 hover:text-blue-600"
            >
              Cost of Living in {cityA.name}
            </Link>
            <Link
              to={`/cost-of-living/${cityB.slug}`}
              className="p-3 bg-slate-50 rounded-lg border border-slate-100 hover:border-blue-200 transition-colors text-center text-sm text-slate-600 hover:text-blue-600"
            >
              Cost of Living in {cityB.name}
            </Link>
            <Link
              to={`/living-in/${cityA.slug}`}
              className="p-3 bg-slate-50 rounded-lg border border-slate-100 hover:border-blue-200 transition-colors text-center text-sm text-slate-600 hover:text-blue-600"
            >
              Living in {cityA.name}
            </Link>
            <Link
              to={`/living-in/${cityB.slug}`}
              className="p-3 bg-slate-50 rounded-lg border border-slate-100 hover:border-blue-200 transition-colors text-center text-sm text-slate-600 hover:text-blue-600"
            >
              Living in {cityB.name}
            </Link>
          </div>
          
          {/* Internal Links to Rankings and Tools */}
          <div className="mt-8 p-6 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl border border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-4">Explore More</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <Link
                to="/rankings"
                className="flex items-center gap-2 text-slate-600 hover:text-blue-600 text-sm"
              >
                <BarChart3 className="w-4 h-4" />
                All City Rankings
              </Link>
              <Link
                to="/cheapest-cities-for-digital-nomads"
                className="flex items-center gap-2 text-slate-600 hover:text-blue-600 text-sm"
              >
                <DollarSign className="w-4 h-4" />
                Cheapest Cities
              </Link>
              <Link
                to="/best-cities-for-digital-nomads"
                className="flex items-center gap-2 text-slate-600 hover:text-blue-600 text-sm"
              >
                <Globe className="w-4 h-4" />
                Best Nomad Cities
              </Link>
              <Link
                to="/cost-of-living-comparison"
                className="flex items-center gap-2 text-slate-600 hover:text-blue-600 text-sm"
              >
                <BarChart3 className="w-4 h-4" />
                Cost Comparison Tool
              </Link>
              <Link
                to="/tools/calculator"
                className="flex items-center gap-2 text-slate-600 hover:text-blue-600 text-sm"
              >
                <DollarSign className="w-4 h-4" />
                Cost Calculator
              </Link>
              <Link
                to="/best-countries-for-expats"
                className="flex items-center gap-2 text-slate-600 hover:text-blue-600 text-sm"
              >
                <Users className="w-4 h-4" />
                Best Countries
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16 bg-slate-50" data-testid="faq-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <HelpCircle className="w-7 h-7 text-blue-600" />
            <h2 className="text-2xl font-bold text-slate-900">
              Frequently Asked Questions: {cityA.name} vs {cityB.name}
            </h2>
          </div>
          
          <div className="space-y-4">
            {generateFAQs().map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* Dynamic Internal Linking Section - SEO Engine */}
      <section className="py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <ComparisonInternalLinks 
            cityASlug={cityA.slug} 
            cityBSlug={cityB.slug} 
            cityAName={cityA.name} 
            cityBName={cityB.name} 
          />
        </div>
      </section>

      <Footer />
    </div>
  );
}

// FAQ Accordion Component
function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-5 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
        data-testid="faq-toggle"
      >
        <span className="font-medium text-slate-900 pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-5 pb-5 text-slate-600 border-t border-slate-100 pt-4">
          {answer}
        </div>
      )}
    </div>
  );
}
